import { type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl' | '6xl' | 'full';
  headerActions?: ReactNode;
  /** Rendered right after the title, e.g. a status toggle belonging to what the title names. */
  titleAddon?: ReactNode;
  /**
   * Replaces the title's default `font-semibold`, rather than adding to it: two font-weight
   * utilities on one element are decided by their order in Tailwind's stylesheet, not in the
   * class attribute, so the override would be a coin toss.
   */
  titleClassName?: string;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = '4xl',
  headerActions,
  titleAddon,
  titleClassName,
}: ModalProps) {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '4xl': 'max-w-4xl',
    '6xl': 'max-w-6xl',
    'full': 'max-w-none w-screen h-screen max-h-screen! rounded-none!',
  };

  const modalContent = (
    <div className={`modal-backdrop ${size === 'full' ? 'p-0!' : ''}`} onClick={onClose}>
      <div
        className={`modal-content ${sizeClasses[size]}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          {/* One flex item, so the header's justify-between keeps the addon beside the title */}
          <div className="flex items-center gap-2 min-w-0">
            <h2 className={`text-lg text-dark-text-primary ${titleClassName || 'font-semibold'}`}>
              {title}
            </h2>
            {titleAddon}
          </div>
          {headerActions && <div className="flex items-center gap-1 ml-auto">{headerActions}</div>}
          <button
            onClick={onClose}
            className="p-1 hover:bg-dark-elevated rounded-sm transition-colors"
          >
            <X className="w-4 h-4 text-dark-text-secondary" />
          </button>
        </div>

        <div className="modal-body">{children}</div>

        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );

  // Use portal to render modal at document.body level, above all other content
  return createPortal(modalContent, document.body);
}
